/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

document.addEventListener('DOMContentLoaded', function() {
    var stickyCartWrapper = document.querySelector('.sticky-cart-wrapper');
    var mobileThreshold = 1000; // Scroll threshold for mobile devices
    var desktopThreshold = 300; // Scroll threshold for desktop
    var scrollThreshold = window.innerWidth <= 768 ? mobileThreshold : desktopThreshold; // Adjust the breakpoint as needed
  
    window.addEventListener('scroll', function() {
      if (window.scrollY > scrollThreshold) {
        stickyCartWrapper.classList.add('visible');
      } else {
        stickyCartWrapper.classList.remove('visible');
      }
    });
  
    // Optional: Update threshold on window resize
    window.addEventListener('resize', function() {
      scrollThreshold = window.innerWidth <= 768 ? mobileThreshold : desktopThreshold;
    });
  });

    document.addEventListener('on:cart-drawer:after-open', (event) => {
      var stickyCartWrapper = document.querySelector('.sticky-cart-wrapper');
      stickyCartWrapper.classList.remove('visible')
    });
    
    document.addEventListener('on:cart-drawer:after-close', (event) => {
      var stickyCartWrapper = document.querySelector('.sticky-cart-wrapper');
      stickyCartWrapper.classList.add('visible')
    });



document.addEventListener('DOMContentLoaded', function () {
    const form = document.querySelector('[data-product-form]');
    
    if (form) {
        form.addEventListener('submit', function (event) {
            event.preventDefault(); // Prevent the default form submission
            
            const formData = new FormData(form);
            const xhr = new XMLHttpRequest();
            xhr.open('POST', '/cart/add.js', true);
            
            // Get the button element and store its original text
            const button = form.querySelector('.btn--sticky-cart');
            const originalButtonText = button.textContent;
            
            // Add loading spinner to the button
            button.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
            
            xhr.onreadystatechange = function () {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        const response = JSON.parse(xhr.responseText);
                        console.log('Product added to cart:', response);
                        // Update the cart UI here (e.g., cart item count, show a confirmation message, etc.)
                        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
                            bubbles: true
                        }));
                        setTimeout(function() {
                            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
                            // Restore the original button text after a delay
                            setTimeout(function() {
                                button.textContent = originalButtonText;
                            }, 1000);
                        }, 1000);
                    } else {
                        console.error('Error adding product to cart:', xhr.responseText);
                    }
                }
            };
            
            xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
            xhr.send(formData);
        });
    } else {
        console.error('Form with data attribute "data-product-form" not found.');
    }
});


// document.addEventListener('DOMContentLoaded', function() {
//   const slider = document.querySelector('.maison-style-slider');
//   const products = slider.querySelectorAll('.maison-style-product');
//   const prevBtn = document.querySelector('.maison-prev-btn');
//   const nextBtn = document.querySelector('.maison-next-btn');

//   let isDragging = false;
//   let startX;
//   let scrollLeft;

//   slider.addEventListener('mousedown', startDragging);
//   slider.addEventListener('touchstart', startDragging, { passive: true });

//   slider.addEventListener('mousemove', drag);
//   slider.addEventListener('touchmove', drag, { passive: true });

//   slider.addEventListener('mouseup', stopDragging);
//   slider.addEventListener('mouseleave', stopDragging);
//   slider.addEventListener('touchend', stopDragging);

//   prevBtn.addEventListener('click', () => scrollToProduct('prev'));
//   nextBtn.addEventListener('click', () => scrollToProduct('next'));

//   function startDragging(e) {
//     isDragging = true;
//     startX = e.pageX || e.touches[0].pageX;
//     scrollLeft = slider.scrollLeft;
//     slider.style.scrollBehavior = 'auto';
//   }

//   function drag(e) {
//     if (!isDragging) return;
//     e.preventDefault();
//     const x = e.pageX || e.touches[0].pageX;
//     const dist = x - startX;
//     slider.scrollLeft = scrollLeft - dist;
//   }

//   function stopDragging() {
//     isDragging = false;
//     slider.style.scrollBehavior = 'smooth';
//     snapToClosestProduct();
//   }

//   function snapToClosestProduct() {
//     const productWidth = products[0].offsetWidth;
//     const scrollPosition = slider.scrollLeft;
//     const targetIndex = Math.round(scrollPosition / productWidth);
//     const targetScroll = targetIndex * productWidth;
//     slider.scrollTo({ left: targetScroll, behavior: 'smooth' });
//   }

//   function scrollToProduct(direction) {
//     const productWidth = products[0].offsetWidth;
//     const currentScroll = slider.scrollLeft;
//     const targetScroll = direction === 'next'
//       ? currentScroll + productWidth
//       : currentScroll - productWidth;
//     slider.scrollTo({ left: targetScroll, behavior: 'smooth' });
//   }

//   // Add to cart functionality for all product forms
//   const forms = document.querySelectorAll('.maison-product-form');
  
//   forms.forEach(form => {
//     form.addEventListener('submit', function (event) {
//       event.preventDefault(); // Prevent the default form submission
      
//       const formData = new FormData(form);
//       const xhr = new XMLHttpRequest();
//       xhr.open('POST', '/cart/add.js', true);
      
//       // Get the button element and store its original text
//       const button = form.querySelector('.maison-add-to-cart-btn');
//       const originalButtonText = button.innerText;
      
//       // Add loading state to the button
//       button.innerText = 'Adding...';
//       button.disabled = true;
      
//       xhr.onreadystatechange = function () {
//         if (xhr.readyState === 4) {
//           if (xhr.status === 200) {
//             const response = JSON.parse(xhr.responseText);
//             console.log('Product added to cart:', response);
            
//             // Dispatch the cart drawer refresh event
//             document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
//               bubbles: true
//             }));
            
//             // Wait for 1 second before opening the cart drawer
//             setTimeout(function() {
//               // Dispatch the cart drawer open event
//               document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
              
//               // Restore the original button text and enable the button
//               setTimeout(function() {
//                 button.innerText = originalButtonText;
//                 button.disabled = false;
//               }, 1000);
//             }, 1000);
//           } else {
//             console.error('Error adding product to cart:', xhr.responseText);
//             button.innerText = originalButtonText;
//             button.disabled = false;
//           }
//         }
//       };
      
//       xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
//       xhr.send(formData);
//     });
//   });
// });

// // Cart drawer JS

// document.addEventListener('DOMContentLoaded', function() {
//   const slider = document.querySelector('.cart-maison-style-slider');
//   const products = slider.querySelectorAll('.cart-maison-style-product');
//   const prevBtn = document.querySelector('.cart-maison-prev-btn');
//   const nextBtn = document.querySelector('.cart-maison-next-btn');

//   // ... (keep the existing slider functionality) ...

//   // Add to cart functionality for all product forms
//   const forms = document.querySelectorAll('.cart-maison-product-form');
  
//   forms.forEach(form => {
//     form.addEventListener('submit', function (event) {
//       event.preventDefault(); // Prevent the default form submission
      
//       const formData = new FormData(form);
//       const xhr = new XMLHttpRequest();
//       xhr.open('POST', '/cart/add.js', true);
      
//       // Get the button element
//       const button = form.querySelector('.cart-maison-add-to-cart-btn');
      
//       // Add loading state to the button
//       button.textContent = 'Adding...';
//       button.disabled = true;
      
//       xhr.onreadystatechange = function () {
//         if (xhr.readyState === 4) {
//           console.log('XHR request completed');
//           if (xhr.status === 200) {
//             const response = JSON.parse(xhr.responseText);
//             console.log('Product added to cart:', response);
            
//             // Dispatch the cart drawer refresh event
//             document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
//               bubbles: true
//             }));
//           } else {
//             console.error('Error adding product to cart:', xhr.responseText);
//           }
          
//           console.log('Setting timeout to reset button');
//           // Restore the button text to "+ Add" and enable the button after 1 second
//           setTimeout(function() {
//             console.log('Timeout callback executed');
//             button.textContent = '+ Add';
//             button.disabled = false;
//             console.log('Button reset to "+ Add" and enabled');
//           }, 1000);
//         }
//       };
      
//       xhr.onerror = function() {
//         console.error('XHR request failed');
//         button.textContent = '+ Add';
//         button.disabled = false;
//       };
      
//       xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
//       xhr.send(formData);
//     });
//   });
// });





// ---------- TEST CODE ------------

document.addEventListener('DOMContentLoaded', function() {
  // Function to initialize a carousel
  function initializeCarousel(sliderClass, productClass, prevBtnClass, nextBtnClass) {
    const slider = document.querySelector(sliderClass);
    if (!slider) return; // Exit if the slider doesn't exist on the page

    const products = slider.querySelectorAll(productClass);
    const prevBtn = document.querySelector(prevBtnClass);
    const nextBtn = document.querySelector(nextBtnClass);

    let isDragging = false;
    let startX;
    let scrollLeft;

    slider.addEventListener('mousedown', startDragging);
    slider.addEventListener('touchstart', startDragging, { passive: true });

    slider.addEventListener('mousemove', drag);
    slider.addEventListener('touchmove', drag, { passive: true });

    slider.addEventListener('mouseup', stopDragging);
    slider.addEventListener('mouseleave', stopDragging);
    slider.addEventListener('touchend', stopDragging);

    if (prevBtn) prevBtn.addEventListener('click', () => scrollToProduct('prev'));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollToProduct('next'));

    function startDragging(e) {
      isDragging = true;
      startX = e.pageX || e.touches[0].pageX;
      scrollLeft = slider.scrollLeft;
      slider.style.scrollBehavior = 'auto';
    }

    function drag(e) {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX || e.touches[0].pageX;
      const dist = x - startX;
      slider.scrollLeft = scrollLeft - dist;
    }

    function stopDragging() {
      isDragging = false;
      slider.style.scrollBehavior = 'smooth';
      snapToClosestProduct();
    }

    function snapToClosestProduct() {
      const productWidth = products[0].offsetWidth;
      const scrollPosition = slider.scrollLeft;
      const targetIndex = Math.round(scrollPosition / productWidth);
      const targetScroll = targetIndex * productWidth;
      slider.scrollTo({ left: targetScroll, behavior: 'smooth' });
    }

    function scrollToProduct(direction) {
      const productWidth = products[0].offsetWidth;
      const currentScroll = slider.scrollLeft;
      const targetScroll = direction === 'next'
        ? currentScroll + productWidth
        : currentScroll - productWidth;
      slider.scrollTo({ left: targetScroll, behavior: 'smooth' });
    }
  }

  // Initialize both carousels
  initializeCarousel('.maison-style-slider', '.maison-style-product', '.maison-prev-btn', '.maison-next-btn');
  initializeCarousel('.cart-maison-style-slider', '.cart-maison-style-product', '.cart-maison-prev-btn', '.cart-maison-next-btn');

  // Add to cart functionality for all product forms (both in main content and cart drawer)
  function initializeAddToCartForms(formClass, btnClass) {
    const forms = document.querySelectorAll(formClass);
    
    forms.forEach(form => {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        
        const formData = new FormData(form);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/cart/add.js', true);
        
        const button = form.querySelector(btnClass);
        const originalButtonText = button.textContent;
        
        button.textContent = 'Adding...';
        button.disabled = true;
        
        xhr.onreadystatechange = function () {
          if (xhr.readyState === 4) {
            console.log('XHR request completed');
            if (xhr.status === 200) {
              const response = JSON.parse(xhr.responseText);
              console.log('Product added to cart:', response);
              
              document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
                bubbles: true
              }));
              
              setTimeout(function() {
                document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
              }, 1000);
            } else {
              console.error('Error adding product to cart:', xhr.responseText);
            }
            
            setTimeout(function() {
              button.textContent = originalButtonText;
              button.disabled = false;
            }, 1000);
          }
        };
        
        xhr.onerror = function() {
          console.error('XHR request failed');
          button.textContent = originalButtonText;
          button.disabled = false;
        };
        
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.send(formData);
      });
    });
  }

  // Initialize add to cart functionality for both main content and cart drawer
  initializeAddToCartForms('.maison-product-form', '.maison-add-to-cart-btn');
  initializeAddToCartForms('.cart-maison-product-form', '.cart-maison-add-to-cart-btn');
});